import { AxiosResponse } from "axios";
import { axiosInstance } from "./common";
import { CaseEvent } from "./events";

export enum FraudAngleRecommendation {
  NO_FRAUD = "NO_FRAUD",
  FRAUD = "FRAUD",
  ATTEMPTED_FRAUD = "ATTEMPTED_FRAUD",
}

export enum IOAppointingAuthority {
  ZONAL_INSPECTORATE = "ZONAL_INSPECTORATE",
  HEAD_OF_INTERNAL_AUDIT = "HEAD_OF_INTERNAL_AUDIT",
  ZFRC = "ZONAL_FRAUD_RECOMMENDATION_COMMITTEE",
}

export const AUTHORITY_DISPLAY_NAMES: Record<IOAppointingAuthority, string> = {
  [IOAppointingAuthority.ZFRC]: "Zonal Manager",
  [IOAppointingAuthority.ZONAL_INSPECTORATE]: "Zonal Inspectorate",
  [IOAppointingAuthority.HEAD_OF_INTERNAL_AUDIT]: "Head of Internal Audit",
};

export enum IndividualAssessor {
  ZONAL_INSPECTORATE = "ZONAL_INSPECTORATE",
  HEAD_OF_INTERNAL_AUDIT = "HEAD_OF_INTERNAL_AUDIT",
}

export enum ZonalCommittee {
  ZFRC = "ZONAL_FRAUD_RECOMMENDATION_COMMITTEE",
}

export interface PostIRComments {
  borrower_or_perpatrator_kyc_assessment?: string | null;
  primary_security_assessment?: string | null;
  collateral_security_assessment?: string | null;
  end_use_of_funds_assessment?: string | null;
  business_operation_assessment?: string | null;
  io_findings_assessment?: string | null;
}

export interface PostIRAssessmentCommon extends PostIRComments {
  assessment_id: string;
  recommendation: FraudAngleRecommendation;
}

export interface AppointingAuthAssessment extends PostIRAssessmentCommon {
  authority: IndividualAssessor;
}

export interface ZonalCommitteeAssessment extends PostIRAssessmentCommon {
  authority: ZonalCommittee;
  meeting_minutes?: string | null;
  deliberation_from_convener?: string | null;
}

export type Assessment = AppointingAuthAssessment | ZonalCommitteeAssessment;

export interface GetPostIRAssessmentsResponse {
  appointing_auth_assessments?: AppointingAuthAssessment[];
  zonal_committee_assessments?: ZonalCommitteeAssessment[];
}

export interface AddAppointingAuthAssessmentRequest extends PostIRComments {
  authority: IndividualAssessor;
  assessment_date: string;
  recommendation: FraudAngleRecommendation;
}

export interface AddZonalCommitteeAssessmentRequest extends PostIRComments {
  authority: ZonalCommittee;
  assessment_date: string;
  recommendation: FraudAngleRecommendation;
  meeting_minutes?: string | null;
  deliberation_from_convener?: string | null;
}

export interface UpdateAppointingAuthAssessmentRequest extends PostIRComments {
  assessment_id: string;
  recommendation?: FraudAngleRecommendation;
}

export interface UpdateZonalCommitteeAssessmentRequest extends PostIRComments {
  assessment_id: string;
  recommendation?: FraudAngleRecommendation;
  meeting_minutes?: string | null;
  deliberation_from_convener?: string | null;
}

export interface SavePostIRAssessmentsRequest {
  add_appointing_auth_assessments?: AddAppointingAuthAssessmentRequest[];
  add_zonal_committee_assessments?: AddZonalCommitteeAssessmentRequest[];
  update_appointing_auth_assessments?: UpdateAppointingAuthAssessmentRequest[];
  update_zonal_committee_assessments?: UpdateZonalCommitteeAssessmentRequest[];
}

export interface SavePostIRAssessmentsResponse {
  appointing_auth_assessments?: AppointingAuthAssessment[];
  zonal_committee_assessments?: ZonalCommitteeAssessment[];
  events?: CaseEvent[];
}

export interface AssessmentUI {
  assessment_id?: string;
  competent_authority?: IOAppointingAuthority;
  assessment_date?: string;
  recommendation?: FraudAngleRecommendation;
  meeting_minutes?: string | null;
  deliberation_from_convener?: string | null;
  borrower_or_perpatrator_kyc_assessment?: string | null;
  primary_security_assessment?: string | null;
  collateral_security_assessment?: string | null;
  end_use_of_funds_assessment?: string | null;
  business_operation_assessment?: string | null;
  io_findings_assessment?: string | null;
  isDirty?: boolean;
  isAdded?: boolean;
}

const ObservationsByAuthorityAPI = {
  get: async (case_id: string): Promise<GetPostIRAssessmentsResponse> => {
    const response: AxiosResponse<GetPostIRAssessmentsResponse> =
      await axiosInstance.get(`/v1/cases/${case_id}/post-ir-assesments`);
    return response.data;
  },

  post: async (
    case_id: string,
    payload: SavePostIRAssessmentsRequest,
  ): Promise<SavePostIRAssessmentsResponse> => {
    const response: AxiosResponse<SavePostIRAssessmentsResponse> =
      await axiosInstance.post(
        `/v1/cases/${case_id}/post-ir-assesments`,
        payload,
      );
    return response.data;
  },
};

export default ObservationsByAuthorityAPI;
